import React, { useEffect, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

export default function AreaYearlyExpenseChart({ years, currency, selectedGlobalYear }) {
  const history = useHistory();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
  const [monthlyExpenseData, setMonthlyExpenseData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
  }, [selectedGlobalYear]);

  // Check login
  useEffect(() => {
    if (!localStorage.getItem("userData")) history.push("/login");
  }, [history]);

  const storedUserData = localStorage.getItem("userData");
  const groupId = storedUserData ? JSON.parse(storedUserData).groupId : "";

  // Dark mode detection
  useEffect(() => {
    const checkDark = () => document.documentElement.classList.contains("dark");
    setIsDarkMode(checkDark());

    const observer = new MutationObserver(() => setIsDarkMode(checkDark()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!groupId) {
        setLoading(false);
        setMonthlyExpenseData([]);
        setCategoryData([]);
        return;
      }

      setLoading(true);
      try {
        const data = await apiService.getYearlyExpense(groupId, selectedYear);
        const categoryExpenses = await apiService.getCategoryExpenses(groupId, selectedYear);

        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        const transformed = months.map((m, idx) => {
          const found = data.find((item) => parseInt(item.month, 10) - 1 === idx);
          return {
            month: m,
            totalDebit: found?.totalDebit || 0,
            totalCredit: found?.totalCredit || 0,
            netBalance: (found?.totalCredit || 0) - (found?.totalDebit || 0),
          };
        });

        const groupedByMonth = categoryExpenses.reduce((acc, e) => {
          const idx = parseInt(e.month, 10) - 1;
          if (!acc[idx]) acc[idx] = {};
          acc[idx][e.expenseDescType] = (acc[idx][e.expenseDescType] || 0) + e.totalExpenses;
          return acc;
        }, {});

        const uniqueCategories = Array.from(new Set(categoryExpenses.map((e) => e.expenseDescType).filter(Boolean)));

        const categoryMap = months.map((m, idx) => {
          const cats = groupedByMonth[idx] || {};
          return uniqueCategories.reduce(
            (acc, cat) => ({ ...acc, [cat]: cats[cat] || 0 }),
            { month: m }
          );
        });

        setMonthlyExpenseData(transformed);
        setCategoryData(categoryMap);
      } catch (err) {
        console.error(err);
        setMonthlyExpenseData([]);
        setCategoryData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, groupId]);

  const tooltipFormatter = useMemo(() => {
    return (params) => {
      if (!params || !params.length) return "";
      const i = params[0]?.dataIndex ?? 0;
      if (!monthlyExpenseData[i]) return "";

      const catInfo = categoryData[i]
        ? Object.entries(categoryData[i])
          .filter(([key]) => key !== "month")
          .map(([cat, val]) => `<div style="color:${isDarkMode ? "#d1d5db" : "#666"};">${cat}: ${currency}${val}</div>`)
          .join("")
        : "";

      const seriesInfo = params
        .map(p => `<div><span style="color:${p.color};font-weight:600;">●</span> ${p.seriesName}: ${currency}${p.value}</div>`)
        .join("");

      return `<strong>${monthlyExpenseData[i].month}</strong><br/>${seriesInfo}<hr/>${catInfo}`;
    };
  }, [monthlyExpenseData, categoryData, isDarkMode, currency]);

  const expensesData = useMemo(() => monthlyExpenseData.map(d => d.totalDebit), [monthlyExpenseData]);
  const creditsData = useMemo(() => monthlyExpenseData.map(d => d.totalCredit), [monthlyExpenseData]);
  const netBalanceData = useMemo(() => monthlyExpenseData.map(d => d.netBalance), [monthlyExpenseData]);
  const xAxisData = useMemo(() => monthlyExpenseData.map(d => d.month), [monthlyExpenseData]);

  const option = useMemo(() => ({
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", crossStyle: { color: "#999" } },
      backgroundColor: isDarkMode ? "#374151" : "#fff",
      borderColor: isDarkMode ? "#4b5563" : "#e5e7eb",
      textStyle: { color: isDarkMode ? "#f9fafb" : "#111827" },
      formatter: tooltipFormatter,
    },
    legend: {
      data: ["Expenses", "Credits", "Net Balance"],
      textStyle: { color: isDarkMode ? "#e5e7eb" : "#374151" },
      align: "right",
      layout: "vertical",
      verticalAlign: "middle",
    },
    xAxis: [{ type: "category", data: xAxisData, axisPointer: { type: "shadow" }, axisLabel: { color: isDarkMode ? "#d1d5db" : "#374151" } }],
    yAxis: [
      {
        type: "value",
        name: `Amount (${currency})`,
        axisLabel: {
          formatter: (value) => `${currency}${value}`,
          color: isDarkMode ? "#d1d5db" : "#374151"
        },
        splitLine: { lineStyle: { color: isDarkMode ? "#4b5563" : "#e5e7eb" } },
      },
      {
        type: "value",
        name: "Net Balance",
        axisLabel: {
          formatter: (value) => `${currency}${value}`,
          color: isDarkMode ? "#d1d5db" : "#374151"
        },
        splitLine: { lineStyle: { color: isDarkMode ? "#4b5563" : "#e5e7eb" } },
      },
    ],
    series: [
      { name: "Expenses", type: "bar", data: expensesData, itemStyle: { color: "#2563eb" } },
      { name: "Credits", type: "bar", data: creditsData, itemStyle: { color: "#22c55e" } },
      { name: "Net Balance", type: "line", yAxisIndex: 1, data: netBalanceData, itemStyle: { color: "#f59e0b" }, smooth: true }
    ],
  }), [tooltipFormatter, xAxisData, expensesData, creditsData, netBalanceData, isDarkMode, currency]);

  return (
    <div className=" bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full mt-4">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 lg:mb-0">Yearly Credit, Debit & Net Balance</h2>
        <select
          style={{
            padding: "6px 15px",
            borderRadius: "6px",
            color: isDarkMode ? "#fff" : "#333",
            border: "solid #ddd 1px",
            minWidth: "80px"
          }}
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Chart */}
      {loading ? (
        <div style={{ height: "400px", display: "flex", justifyContent: "center", alignItems: "center", color: isDarkMode ? "#ccc" : "#666" }}>
          Loading chart...
        </div>
      ) : monthlyExpenseData.length === 0 ? (
        <div style={{ height: "400px", display: "flex", justifyContent: "center", alignItems: "center", color: isDarkMode ? "#ccc" : "#666" }}>
          No data available for selected year.
        </div>
      ) : (
        <ReactECharts option={option} style={{ height: "400px" }} />
      )}
    </div>
  );
}
